'use client';

import { useParams } from 'next/navigation';
import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { useSession } from 'next-auth/react';
import {
  holdsAllScopedPermissions,
  holdsAnyScopedPermission,
  holdsScopedPermissionFrom,
} from '@/lib/permissions/scopeMatch';
import { createLatestRequestGuard } from '@/lib/permissions/latestRequestGuard';

interface PermissionsContextValue {
  permissions: string[];
  loading: boolean;
  hasPermission: (permissionCode: string) => boolean;
  hasAnyPermission: (permissionCodes: string[]) => boolean;
  hasAllPermissions: (permissionCodes: string[]) => boolean;
  isAuthenticated: boolean;
  refetch: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | undefined>(undefined);

interface PermissionsProviderProps {
  children: ReactNode;
}

const CACHE_KEY = 'user_permissions_cache';
const CACHE_DURATION = 2 * 60 * 1000; // Reduced to 2 minutes to avoid stale data

interface CachedPermissions {
  permissions: string[];
  timestamp: number;
  userId: string;
  sessionId: string; // Add session ID to detect session changes
}

/**
 * Provider that caches user permissions to avoid multiple API calls
 *
 * Features:
 * - Caches permissions in memory and localStorage
 * - Automatically invalidates cache after 2 minutes
 * - Invalidates cache when user/session changes
 * - Provides refetch function to manually refresh permissions
 * - One load per user/session/tenant key at a time; a load for an older key
 *   (e.g. the tenant the user just left) never overwrites the newer one
 */
export function PermissionsProvider({ children }: PermissionsProviderProps) {
  const params = useParams<{ tenant: string }>();
  const tenant = params?.tenant;

  const { data: session, status } = useSession();
  const [permissions, setPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const requestGuard = useRef(createLatestRequestGuard());
  const lastSessionId = useRef<string | null>(null);

  // Load permissions from cache or API
  const loadPermissions = useCallback(async (skipCache = false) => {
    if (status === 'loading') {
      return;
    }

    if (!session?.user?.id) {
      // A fresh guard makes any load still in flight for the old session stale.
      requestGuard.current = createLatestRequestGuard();
      setPermissions([]);
      setLoading(false);
      lastSessionId.current = null;
      // Clear cache when user logs out
      if (typeof window !== 'undefined') {
        localStorage.removeItem(CACHE_KEY);
      }
      return;
    }

    // Generate a unique session ID from user ID and login time
    // The tenant is part of the key: permissions are per tenant, so moving to
    // another tenant must refetch instead of reusing the previous tenant's list.
    const currentSessionId = `${session.user.id}-${session.expires || Date.now()}-${tenant ?? ''}`;

    // If session changed, invalidate cache and refetch
    if (lastSessionId.current && lastSessionId.current !== currentSessionId) {
      skipCache = true;
      if (typeof window !== 'undefined') {
        localStorage.removeItem(CACHE_KEY);
      }
    }
    lastSessionId.current = currentSessionId;

    // Skip a duplicate load of this same key, but never a load for a new
    // tenant because the previous tenant's load is still running.
    const requestKey = currentSessionId;
    if (!requestGuard.current.begin(requestKey)) {
      return;
    }
    const isLatest = () => requestGuard.current.isLatest(requestKey);

    try {

      // Check cache first (if not forcing refresh)
      if (!skipCache && typeof window !== 'undefined') {
        const cachedData = localStorage.getItem(CACHE_KEY);
        if (cachedData) {
          try {
            const parsed: CachedPermissions = JSON.parse(cachedData);
            const now = Date.now();
            const isValid =
              parsed.userId === session.user.id &&
              parsed.sessionId === currentSessionId &&
              parsed.timestamp &&
              (now - parsed.timestamp) < CACHE_DURATION;

            if (isValid && Array.isArray(parsed.permissions)) {
              // Use cached permissions
              console.log('📦 Using cached permissions:', parsed.permissions.length);
              if (isLatest()) {
                setPermissions(parsed.permissions);
              }
              return;
            }
          } catch (parseError) {
            // Invalid cache, remove it
            console.warn('Invalid cache, removing:', parseError);
            localStorage.removeItem(CACHE_KEY);
          }
        }
      }

      // Fetch from API
      console.log('🔄 Fetching permissions from API...');
      const permissionsUrl = tenant
        ? `/api/auth/me/permissions?tenant=${encodeURIComponent(tenant)}`
        : '/api/auth/me/permissions';
      const response = await fetch(permissionsUrl, {
        // Disable browser caching
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      // The user moved to another tenant while this was in flight: its
      // answer belongs to the tenant he left.
      if (!isLatest()) {
        return;
      }

      if (response.ok) {
        const data = await response.json();
        if (!isLatest()) {
          return;
        }
        const fetchedPermissions = data.permissions || [];
        console.log('✅ Fetched permissions:', fetchedPermissions.length);
        setPermissions(fetchedPermissions);

        // Cache the permissions
        if (typeof window !== 'undefined') {
          const cacheData: CachedPermissions = {
            permissions: fetchedPermissions,
            timestamp: Date.now(),
            userId: session.user.id,
            sessionId: currentSessionId,
          };
          localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        }
      } else {
        console.error('❌ Failed to fetch permissions:', response.status);
        setPermissions([]);
        // Clear invalid cache
        if (typeof window !== 'undefined') {
          localStorage.removeItem(CACHE_KEY);
        }
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      if (!isLatest()) {
        return;
      }
      setPermissions([]);
      // Clear cache on error
      if (typeof window !== 'undefined') {
        localStorage.removeItem(CACHE_KEY);
      }
    } finally {
      requestGuard.current.end(requestKey);
      if (isLatest()) {
        setLoading(false);
      }
    }
  }, [session?.user?.id, session?.expires, status, tenant]);

  // Load permissions on mount and when session changes
  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  // Refetch function to manually refresh permissions
  const refetch = useCallback(async () => {
    setLoading(true);
    await loadPermissions(true); // Skip cache
  }, [loadPermissions]);

  // Scope-aware, same rule as withTenantPermission: a farm caretaker holds
  // `...:create:farm`, and an exact includes() hid every button the API
  // would have accepted from him.
  const hasPermission = useCallback((permissionCode: string): boolean => {
    return holdsScopedPermissionFrom(permissions, permissionCode);
  }, [permissions]);

  const hasAnyPermission = useCallback((permissionCodes: string[]): boolean => {
    return holdsAnyScopedPermission(permissions, permissionCodes);
  }, [permissions]);

  const hasAllPermissions = useCallback((permissionCodes: string[]): boolean => {
    return holdsAllScopedPermissions(permissions, permissionCodes);
  }, [permissions]);

  const value: PermissionsContextValue = {
    permissions,
    loading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isAuthenticated: !!session?.user,
    refetch,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

/**
 * Hook to access permissions from context
 *
 * @throws Error if used outside of PermissionsProvider
 *
 * @example
 * const { hasPermission, loading } = usePermissionsContext();
 *
 * if (hasPermission('adminPanel:farm:create')) {
 *   // Show create button
 * }
 */
export function usePermissionsContext() {
  const context = useContext(PermissionsContext);

  if (context === undefined) {
    throw new Error('usePermissionsContext must be used within a PermissionsProvider');
  }

  return context;
}
